import { initialAutomationErrorState } from 'src/assets/AutomationsConfig'
import {
  AutomatedActions,
  AutomatedActionType,
  AutomationDiscountDataType,
  AutomationFlowJourneySteps,
  AutomationStateType,
  PerformedActionsOptions,
  PerformedActionType
} from '../types/automations'
import { CartConditionOptions, PurchaseOptions } from '../types/common/cartConditions'
import {
  CustomerEligibilityOptions,
  CustomerEligibilityStateType,
  RtoRiskStrategyOptions,
  utmDataOptions,
  UTMSelectorStateType
} from '../types/common/customerConditions'
import { parseCartConditionsResponse } from './cartConditionsHelpers'

const getAutomationType = (type: AutomatedActions) => {
  switch (type) {
    case AutomatedActions.DISABLE_REWARDS:
      return 'DISABLE_REWARDS'
    case AutomatedActions.HIDE_SPLIT_COD:
      return 'DISABLE_SPLIT_COD'
    case AutomatedActions.HIDE_PAYMENT_MODES:
      return 'DISABLE_PAYMENT'
  }
}

const getDisabledEntities = (state: AutomationStateType) => {
  switch (state.automatedAction.type) {
    case AutomatedActions.HIDE_PAYMENT_MODES:
      return state.automatedAction.paymentMethodIds
    case AutomatedActions.DISABLE_REWARDS:
      return ['LOYALTY', 'REWARDS_WALLET']
    case AutomatedActions.HIDE_SPLIT_COD:
      return ['SPLIT_COD']
  }
}

export const getUTMPayloadKey = (key: utmDataOptions) => {
  switch (key) {
    case utmDataOptions.UTM_SOURCE:
      return 'source'
    case utmDataOptions.UTM_CAMPAIGN:
      return 'campaign'
    case utmDataOptions.UTM_MEDIUM:
      return 'medium'
    case utmDataOptions.UTM_CONTENT:
      return 'content'
  }
}

export const getAutomationsUTMPayloadKey = (key: utmDataOptions) => {
  switch (key) {
    case utmDataOptions.UTM_SOURCE:
      return 'utm_source'
    case utmDataOptions.UTM_CAMPAIGN:
      return 'utm_campaign'
    case utmDataOptions.UTM_MEDIUM:
      return 'utm_medium'
    case utmDataOptions.UTM_CONTENT:
      return 'utm_content'
  }
}

export const getAutomationsUTMStateKey = (key: string): utmDataOptions => {
  switch (key) {
    case 'utm_source':
      return utmDataOptions.UTM_SOURCE
    case 'utm_campaign':
      return utmDataOptions.UTM_CAMPAIGN
    case 'utm_medium':
      return utmDataOptions.UTM_MEDIUM
    case 'utm_content':
      return utmDataOptions.UTM_CONTENT
    default:
      return utmDataOptions.UTM_SOURCE
  }
}


export const getUTMStateKey = (key: string): utmDataOptions => {
  switch (key) {
    case 'source':
      return utmDataOptions.UTM_SOURCE
    case 'campaign':
      return utmDataOptions.UTM_CAMPAIGN
    case 'medium':
      return utmDataOptions.UTM_MEDIUM
    case 'content':
      return utmDataOptions.UTM_CONTENT
    default:
      return utmDataOptions.UTM_SOURCE
  }
}

const getExecutionConditions = (state: AutomationStateType) => {
  const executionConditions = []
  if (state.cartConditions.type !== CartConditionOptions.NO_CONDITIONS) {
    if (state.cartConditions.selectedOption.type === PurchaseOptions.ENTIRE_CART) {
      executionConditions.push({
        type: 'CART_CONDITIONS',
        ...(state.cartConditions.type === CartConditionOptions.PURCHASE_AMOUNT
          ? {
              minimum_amount: state.cartConditions.selectedOption.minimumValue,
              maximum_amount: state.cartConditions.selectedOption.maximumEnabled
                ? state.cartConditions.selectedOption.maximumValue
                : null
            }
          : {
              minimum_quantity: state.cartConditions.selectedOption.minimumValue,
              maximum_quantity: state.cartConditions.selectedOption.maximumEnabled
                ? state.cartConditions.selectedOption.maximumValue
                : null
            })
      })
    }
    if (state.cartConditions.selectedOption.type !== PurchaseOptions.ENTIRE_CART) {
      executionConditions.push({
        type: 'CART_CONDITIONS',
        prerequisites: state.cartConditions.selectedOption.quantityDistribution.quantityDistributionItems.map(item => {
          return {
            ...(state.cartConditions.type === CartConditionOptions.PURCHASE_AMOUNT
              ? {
                  price: item.minimumValue,
                  max_price: item.maximumEnabled ? item.maximumValue : null
                }
              : {
                  quantity: item.minimumValue,
                  max_quantity: item.maximumEnabled ? item.maximumValue : null
                }),
            ...(item.catalogType === PurchaseOptions.SPECIFIC_PRODUCTS
              ? {
                  entities: [
                    { type: 'PRODUCT', entity_id: item.productIds },
                    { type: 'VARIANT', entity_id: item.variantIds }
                  ]
                }
              : {
                  entities: [{ type: 'COLLECTION', entity_id: item.collectionIds }]
                })
          }
        })
      })
    }
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.FIRST_TIME_CUSTOMERS) {
    executionConditions.push({
      type: 'ORDER_COUNT_ELIGIBILITY',
      user_order_cohort: {
        lifetime_min_order_count: 0,
        lifetime_max_order_count: 0
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.LIFETIME_ORDER_COUNT) {
    executionConditions.push({
      type: 'ORDER_COUNT_ELIGIBILITY',
      user_order_cohort: {
        lifetime_min_order_count: state.customerEligibility.minimumOrders,
        ...(state.customerEligibility.maximumEnabled
          ? { lifetime_max_order_count: state.customerEligibility.maximumOrders }
          : {})
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.CUSTOMER_SEGMENT) {
    executionConditions.push({
      type: 'SPECIFIC_SEGMENTS',
      segment_ids: state.customerEligibility.segmentIds
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_CUSTOMERS) {
    executionConditions.push({
      type: 'FILE_UPLOAD_ELIGIBILITY',
      segment_type: 'PHONE_NUMBER',
      file_meta: {
        file_url: state.customerEligibility.uploadedFile?.url,
        file_name: state.customerEligibility.uploadedFile?.name,
        size: state.customerEligibility.uploadedFile?.size
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_PINCODES) {
    executionConditions.push({
      type: 'FILE_UPLOAD_ELIGIBILITY',
      segment_type: 'PINCODE',
      file_meta: {
        file_url: state.customerEligibility.uploadedFile?.url,
        file_name: state.customerEligibility.uploadedFile?.name,
        size: state.customerEligibility.uploadedFile?.size
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.UTM_PARAMS) {
    const utmObject: Record<string, string[]> = {}
    state.customerEligibility.utmData.forEach((item: UTMSelectorStateType) => {
      utmObject[getAutomationsUTMPayloadKey(item.key)] = item.value
    })
    executionConditions.push({
      type: 'UTM_PARAMS',
      utm_params: utmObject
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.PINCODE_RTO_RISK) {
    executionConditions.push({
      type: 'RTO_CONDITIONS',
      score_type: 'PINCODE_RISK_SCORE',
      ...(state.customerEligibility.strategy === RtoRiskStrategyOptions.GREATER_THAN
        ? {
            min_value: state.customerEligibility.score
          }
        : {
            max_value: state.customerEligibility.score
          })
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.CUSTOMER_RTO_RISK) {
    executionConditions.push({
      type: 'RTO_CONDITIONS',
      score_type: 'RTO_RISK_SCORE',
      ...(state.customerEligibility.strategy === RtoRiskStrategyOptions.GREATER_THAN
        ? {
            min_value: Number(state.customerEligibility.score) / 100
          }
        : {
            max_value: Number(state.customerEligibility.score) / 100
          })
    })
  }

  if (state.performedAction.type === PerformedActionsOptions.REDEEM_REWARDS) {
    executionConditions.push({
      type: 'CUSTOMER_ACTION',
      customer_action: 'ON_APPLY_REWARDS'
    })
  }
  if (state.performedAction.type === PerformedActionsOptions.APPLY_DISCOUNTS) {
    executionConditions.push({
      type: 'CUSTOMER_ACTION',
      customer_action: 'ON_APPLY_DISCOUNTS',
      entity_ids: state.performedAction.appliedDiscounts
    })
  }
  return executionConditions
}

const createMetaData = (state: AutomationStateType) => {
  const metadata = {
    ...(state.cartConditions.type !== CartConditionOptions.NO_CONDITIONS &&
    state.cartConditions.selectedOption.type !== PurchaseOptions.ENTIRE_CART
      ? {
          prerequisites: state.cartConditions.selectedOption.quantityDistribution.quantityDistributionItems.map(
            item => item.catalogueMetadata
          )
        }
      : {}),
    ...(state.performedAction.type === PerformedActionsOptions.APPLY_DISCOUNTS
      ? {
          discounts_metadata: state.performedAction.discountsMetadata
        }
      : {}),
    ...(state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_CUSTOMERS ||
    state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_PINCODES
      ? {
          uploaded_file_data: state.customerEligibility.uploadedFile
        }
      : {})
  }
  return metadata
}

export const createAutomationPayload = (state: AutomationStateType) => {
  const payload = {
    name: state.title,
    description: Boolean(state.description) ? state.description : '',
    type: getAutomationType(state.automatedAction.type),
    disabled_entities: getDisabledEntities(state),
    execution_conditions_v2: getExecutionConditions(state),
    is_active: state.isActive,
    start_time: state.activePeriod.startDate.getTime(),
    ...(state.activePeriod.endDate ? { end_time: state.activePeriod.endDate.getTime() } : {}),
    metadata: createMetaData(state)
  }
  return payload
}

export const parseAutomationResponse = (response: any): AutomationStateType => {
  const getAutomatedAction = (): AutomatedActionType => {
    switch (response?.type) {
      case 'DISABLE_REWARDS':
        return { type: AutomatedActions.DISABLE_REWARDS }
      case 'DISABLE_PAYMENT':
        return { type: AutomatedActions.HIDE_PAYMENT_MODES, paymentMethodIds: response?.disabled_entities }
      case 'DISABLE_SPLIT_COD':
        return { type: AutomatedActions.HIDE_SPLIT_COD }
      default:
        return { type: AutomatedActions.DISABLE_REWARDS }
    }
  }

  const getPerformedAction = (): PerformedActionType => {
    const customerActionObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'CUSTOMER_ACTION'
    )
    if (!customerActionObject) {
      return { type: PerformedActionsOptions.NO_CONDITIONS }
    }
    if (customerActionObject?.customer_action === 'ON_APPLY_REWARDS') {
      return { type: PerformedActionsOptions.REDEEM_REWARDS }
    }
    if (customerActionObject?.customer_action === 'ON_APPLY_DISCOUNTS') {
      return {
        type: PerformedActionsOptions.APPLY_DISCOUNTS,
        appliedDiscounts: customerActionObject?.entity_ids,
        discountsMetadata: response?.metadata?.discounts_metadata
          ? response?.metadata?.discounts_metadata
          : customerActionObject?.metadata?.discount_details?.map((data: any): AutomationDiscountDataType => {
              return {
                code: data?.code,
                id: data?.id,
                title: data?.title,
                couponId: data?.couponId,
                isActive: data?.isActive
              }
            })
      }
    }
    return { type: PerformedActionsOptions.NO_CONDITIONS }
  }

  const getCustomerEligibility = (): CustomerEligibilityStateType => {
    const orderCountEligibilityObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'ORDER_COUNT_ELIGIBILITY'
    )
    if (orderCountEligibilityObject) {
      if (
        orderCountEligibilityObject?.user_order_cohort?.lifetime_min_order_count === 0 &&
        orderCountEligibilityObject?.user_order_cohort?.lifetime_max_order_count === 0
      ) {
        return { type: CustomerEligibilityOptions.FIRST_TIME_CUSTOMERS }
      }
      if (
        orderCountEligibilityObject?.user_order_cohort?.lifetime_min_order_count ||
        orderCountEligibilityObject?.user_order_cohort?.lifetime_max_order_count
      ) {
        return {
          type: CustomerEligibilityOptions.LIFETIME_ORDER_COUNT,
          minimumOrders: orderCountEligibilityObject?.user_order_cohort?.lifetime_min_order_count ?? 0,
          maximumEnabled: Boolean(orderCountEligibilityObject?.user_order_cohort?.lifetime_max_order_count),
          maximumOrders: orderCountEligibilityObject?.user_order_cohort?.lifetime_max_order_count ?? 0
        }
      }
    }

    const segmentEligibilityObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'SPECIFIC_SEGMENTS'
    )
    if (segmentEligibilityObject) {
      return { type: CustomerEligibilityOptions.CUSTOMER_SEGMENT, segmentIds: segmentEligibilityObject?.segment_ids }
    }

    const fileUploadObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'FILE_UPLOAD_ELIGIBILITY'
    )
    if (fileUploadObject) {
      return {
        type:
          fileUploadObject?.segment_type === 'PHONE_NUMBER'
            ? CustomerEligibilityOptions.SPECIFIC_CUSTOMERS
            : CustomerEligibilityOptions.SPECIFIC_PINCODES,
        uploadedFile: response?.metadata?.uploaded_file_data
      }
    }

    const utmParamsObject = response?.execution_conditions_v2?.find((condition: any) => condition.type === 'UTM_PARAMS')
    if (utmParamsObject) {
      const utmDataArray = Object.entries(utmParamsObject?.utm_params).map(
        ([key, value]): UTMSelectorStateType => ({
          key: getAutomationsUTMStateKey(key),
          value: value as string[]
        })
      )
      return { type: CustomerEligibilityOptions.UTM_PARAMS, utmData: utmDataArray }
    }

    const rtoRiskObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'RTO_CONDITIONS'
    )
    if (rtoRiskObject) {
      const score =
        Number(Boolean(rtoRiskObject?.min_value) ? rtoRiskObject?.min_value : rtoRiskObject?.max_value) *
        (rtoRiskObject?.score_type === 'PINCODE_RISK_SCORE' ? 1 : 100)
      return {
        type:
          rtoRiskObject?.score_type === 'PINCODE_RISK_SCORE'
            ? CustomerEligibilityOptions.PINCODE_RTO_RISK
            : CustomerEligibilityOptions.CUSTOMER_RTO_RISK,
        strategy: Boolean(rtoRiskObject?.min_value)
          ? RtoRiskStrategyOptions.GREATER_THAN
          : RtoRiskStrategyOptions.LESS_THAN,
        score: score
      }
    }
    return { type: CustomerEligibilityOptions.ALL_CUSTOMERS }
  }

  const cartConditionsObject = response?.execution_conditions_v2?.find(
    (condition: any) => condition.type === 'CART_CONDITIONS'
  )
  const state: AutomationStateType = {
    title: response?.name ?? '',
    description: response?.description ?? '',
    automatedAction: getAutomatedAction(),
    cartConditions: parseCartConditionsResponse(
      cartConditionsObject?.minimum_quantity,
      cartConditionsObject?.maximum_quantity,
      cartConditionsObject?.minimum_amount,
      cartConditionsObject?.maximum_amount,
      cartConditionsObject?.prerequisites,
      response?.metadata?.prerequisites
    ),
    performedAction: getPerformedAction(),
    customerEligibility: getCustomerEligibility(),
    activePeriod: {
      startDate: Boolean(response?.start_time)
        ? new Date(response?.start_time)
        : Boolean(response?.updated_at || response?.created_at)
        ? new Date(response?.updated_at ? response?.updated_at : response?.created_at)
        : new Date(),
      endDateEnabled: Boolean(response?.end_time),
      endDate: response?.end_time && new Date(response?.end_time)
    },
    isActive: response?.is_active,
    errorState: initialAutomationErrorState,
    journeyStep: AutomationFlowJourneySteps.SETUP
  }
  return state
}
